import React, { useState, useEffect } from "react";
import { Table, Button, Input, notification } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import * as XLSX from "xlsx";
import { columnDefinitionsArray } from "./ChartsTables";
import { Helmet } from "react-helmet";

// ✅ Transform Data
const metricsArray = columnDefinitionsArray.map((item, index) => ({
  key: index + 1, // Unique key for Ant Design Table
  columnName: (item["Column Name"] || item["**Column Name**"] || "").replace(
    /\*\*/g,
    ""
  ),
  description: item["Description"] || item["**Description**"],
  sampleValue: item["Sample Value"] || item["**Sample Value**"],
  measurementUnit: item["Measurement Unit"] || item["**Measurement Unit**"],
}));

// ✅ Notification on Copy
const openNotification = (columnName, sampleValue) => {
  notification.success({
    message: "Copied to Clipboard",
    description: `${columnName} = ${sampleValue}`,
    placement: "topRight",
    duration: 2, // Display duration in seconds
  });
};

// ✅ Table Columns
const columns = [
  {
    title: "Metrics",
    dataIndex: "columnName",
    key: "columnName",
    width: 200,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 300,
  },
  {
    title: "Sample Value",
    dataIndex: "sampleValue",
    key: "sampleValue",
    width: 180,
  },
  {
    title: "Measurement Unit",
    dataIndex: "measurementUnit",
    key: "measurementUnit",
    width: 180,
  },
  {
    title: "Actions",
    key: "actions",
    width: 120,
    render: (text, record) => {
      const textToCopy = `${record.columnName} = ${record.sampleValue}`;
      return (
        <CopyToClipboard
          text={textToCopy}
          onCopy={() => openNotification(record.columnName, record.sampleValue)}
        >
          <Button icon={<CopyOutlined />} size="small">
            Copy
          </Button>
        </CopyToClipboard>
      );
    },
  },
];

const Metrics = () => {
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ✅ Filtered Data
  const filteredRows = metricsArray.filter((row) =>
    Object.values(row).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(search.toLowerCase())
    )
  );

  // ✅ Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(metricsArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Metrics");
    XLSX.writeFile(workbook, "Metrics.xlsx");
  };

  // ✅ Detect Screen Resizing
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      style={{
        padding: isMobile ? "10% 5%" : "5%",
        marginTop: isMobile ? "10%" : "0%",
        marginBottom: isMobile ? "10%" : "0%",
        background: "#fff",
      }}
    >
      <Helmet>
        <title>
          Explore Stock Metrics - Interactive SQL Dashboard | Stockbuzz.ai
        </title>
        <meta
          name="description"
          content="Discover and analyze over 3,000 US-traded stocks using 300+ metrics. Query in plain English or customize SQL to filter companies with latest data."
        />
      </Helmet>

      <h1>Metrics Dashboard</h1>

      {/* ✅ Description Box */}
      <div
        style={{
          margin: "20px 0",
          fontSize: "small",
          color: "#333",
          backgroundColor: "#f9f9f9",
          padding: "15px",
          borderRadius: "8px",
          textAlign: "justify",
          lineHeight: "1.6",
        }}
      >
        Welcome to our Metrics Dashboard, where you can explore insights from
        over 3,000 US-traded stocks using 300+ metrics. You can intuitively
        query our database in plain English, and we generate precise SQL queries
        to help analyze company data.
      </div>

      {/* ✅ Search & Export Buttons */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <Input.Search
          placeholder="Search Metrics..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: "300px" }}
        />
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportToExcel}
        >
          Export to Excel
        </Button>
      </div>

      {/* ✅ Ant Design Table */}
      <Table
        dataSource={filteredRows}
        columns={columns}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
        bordered
      />
    </div>
  );
};

export default Metrics;
